"use client"

import * as React from "react"
import { Progress as ProgressPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

const TONE = {
  default: "bg-primary",
  neutral: "bg-muted-foreground/40",
  info: "bg-info",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
} as const

/**
 * `tone` colours the filled part by what it means: `danger` for over a limit (a container over full), `info` for work in progress.
 * `size="sm"` is the thin bar for figures in a card or field. `marker` (0 to 100) adds a tick where the figure is heading
 * (a weekly limit's projection at the reset), with `markerLabel` as its hover text.
 */
function Progress({
  className,
  value,
  tone = "default",
  size = "default",
  marker,
  markerLabel,
  ...props
}: React.ComponentProps<typeof ProgressPrimitive.Root> & {
  tone?: keyof typeof TONE
  size?: "default" | "sm"
  marker?: number | null
  markerLabel?: string
}) {
  const bar = (
    <ProgressPrimitive.Root
      data-slot="progress"
      className={cn(
        "relative w-full overflow-hidden rounded-full bg-primary/20",
        size === "sm" ? "h-1.5" : "h-2",
        marker === null || marker === undefined ? className : undefined
      )}
      {...props}
    >
      <ProgressPrimitive.Indicator
        data-slot="progress-indicator"
        data-tone={tone}
        className={cn("h-full w-full flex-1 transition-all", TONE[tone])}
        style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
      />
    </ProgressPrimitive.Root>
  )
  if (marker === null || marker === undefined) return bar
  // The bar clips its children, so the tick sits beside it in a wrapper that takes the className.
  return (
    <span className={cn("relative block w-full", className)}>
      {bar}
      <span
        data-slot="progress-marker"
        className={cn(
          "absolute w-0.5 -translate-x-1/2 rounded-full bg-foreground/70",
          size === "sm" ? "-top-0.5 h-2.5" : "-top-0.5 h-3"
        )}
        style={{ left: `${Math.min(100, Math.max(0, marker))}%` }}
        title={markerLabel}
        aria-hidden
      />
    </span>
  )
}

export { Progress }
